import { coloursServiceInstance } from "../services";

export enum ColourType {
    None = "none",
    Registration = "registration",
    ProcessColour = "processcol",
    CustomSpot = "customspot",
    RegisteredSpot = "spotcol",
}

export const COLOUR_TYPE_NAME_MAP = {
    [ColourType.None]: "None",
    [ColourType.Registration]: "Registration",
    [ColourType.ProcessColour]: "Process Colour",
    [ColourType.RegisteredSpot]: "Spot Colour",
    [ColourType.CustomSpot]: "[DEBUG] Custom Spot Colour",
}

export function registrationToRgba(registration: number, alpha: number = 1): IRGBAColour {
    const regVal = (1.0 - Math.max(0.0, Math.min(1.0, registration / 100.0))) * 255.0;
    return {
        r: regVal,
        g: regVal,
        b: regVal,
        a: alpha,
    }
}

export function registrationToCss(registration: number, alpha: number = 1): string {
    return rgbaToCss(registrationToRgba(registration, alpha));
}

export function rgbaToCss(r: number | IRGBAColour | null, g: number = 0, b: number = 0, a: number = 1): string {
    if (r === null) {
        // TODO: Correct keyword is transparent in HTML CSS, but not in SVG.
        return `none`;
    }

    if (typeof r !== "number") {
        return rgbaToCss(r.r, r.g, r.b, r.a);
    }

    if (a <= 0) {
        return `none`;
    } else {
        return `rgba(${r}, ${g}, ${b}, ${a})`;
    }
}

export function rgbaToCmyka(r: number | IRGBColour | IRGBAColour, g: number = 0, b: number = 0, a: number = 0): ICMYKAColour {
    if (typeof r !== 'number') {
        if ('a' in r) {
            a = r.a;
        }
        b = r.b;
        g = r.g;
        r = r.r;
    }

    let c = 1 - (r / 255);
    let m = 1 - (g / 255);
    let y = 1 - (b / 255);
    let k = Math.min(c, Math.min(m, y));

    c = (c - k) / (1 - k);
    m = (m - k) / (1 - k);
    y = (y - k) / (1 - k);

    c = isNaN(c) ? 0 : c;
    m = isNaN(m) ? 0 : m;
    y = isNaN(y) ? 0 : y;
    k = isNaN(k) ? 0 : k;

    return {
        c: c,
        m: m,
        y: y,
        k: k,
        a: a,
    }
}

export function cmykaToCss(c: number | ICMYKColour | ICMYKAColour, m: number = 0, y: number = 0, k: number = 0, a: number = 0): string {
    if (typeof c !== "number" && (c as any).a !== undefined) {
        a = (c as any).a;
    }

    return rgbaToCss({ ...cmykToRgb(c, m, y, k), a: a });
}

export function cmykToRgb(c: number | ICMYKColour, m ?: number, y ?: number, k ?: number, normalized: boolean = false): IRGBColour {
    if (typeof c !== "number") {
        k = c.k;
        y = c.y;
        m = c.m;
        c = c.c;
    }

    c = Math.max(0.0, Math.min(1.0, c));
    m = Math.max(0.0, Math.min(1.0, m));
    y = Math.max(0.0, Math.min(1.0, y));
    k = Math.max(0.0, Math.min(1.0, k));

    c = c * (1 - k) + k;
    m = m * (1 - k) + k;
    y = y * (1 - k) + k;

    let r = 1 - c;
    let g = 1 - m;
    let b = 1 - y;

    if (!normalized) {
        r = Math.round(255 * r);
        g = Math.round(255 * g);
        b = Math.round(255 * b);
    }

    return {
        r: r,
        g: g,
        b: b
    }
}

export function cmykaToRgba(c: number | ICMYKAColour, m: number, y: number, k: number, alpha: number = 1.0, normalized: boolean = false): IRGBAColour {
    if (typeof c !== "number") {
        alpha = c.a;
        k = c.k;
        y = c.y;
        m = c.m;
        c = c.c;
    }

    return { ...cmykToRgb(c, m, y, k, normalized), a: alpha };
}

export function colourDataToRGBA(colourData: IColourData): IRGBAColour {
    switch (colourData.type) {
        case ColourType.Registration:
            return registrationToRgba(colourData.reg);

        case ColourType.RegisteredSpot:
            for (const spot of coloursServiceInstance.spotColours) {
                if (spot.id === colourData.spotID) {
                    return cmykaToRgba(spot.c * colourData.tint, spot.m * colourData.tint, spot.y * colourData.tint, spot.k * colourData.tint, colourData.alpha);
                }
            }

            console.warn(`Could not find a registered spot colour with ID ${colourData.spotID}`);
            return { r: 0, g: 0, b: 0, a: 0 };

        case ColourType.ProcessColour:
        case ColourType.CustomSpot:
            return cmykaToRgba(colourData.c, colourData.m, colourData.y, colourData.k, colourData.alpha);

        case ColourType.None:
            return { r: 0, g: 0, b: 0, a: 0 };

        default:
            throw new Error(`Unknown PD colour string type ${(colourData as any).type}`);
    }
}

export function cssColourToRgba(colour: string): IRGBAColour {
    if (!colour || colour.length == 0) {
        return { r: 0, g: 0, b: 0, a: 0 };
    }

    colour = colour.trim().toLowerCase();
    if (colour === "transparent") {
        return { r: 0, g: 0, b: 0, a: 0 };
    }

    if (colour.startsWith('#')) {
        if (colour.length < 7) {
            // convert #RGB and #RGBA to #RRGGBB and #RRGGBBAA
            colour = '#' + colour[1] + colour[1]
                + colour[2] + colour[2]
                + colour[3] + colour[3]
                + (colour.length > 4 ? colour[4] + colour[4] : '');
        }

        return {
            r: parseInt(colour.substring(1, 1 + 2), 16),
            g: parseInt(colour.substring(3, 3 + 2), 16),
            b: parseInt(colour.substring(5, 5 + 2), 16),
            a: colour.length > 7 ? parseInt(colour.substring(7, 7 + 2), 16) / 255 : 1,
        };
    }

    if (colour.startsWith('rgb')) {
        if (colour.indexOf('rgba') === -1) {
            colour += ',1'; // convert 'rgb(R,G,B)' to 'rgb(R,G,B)A' which looks awful but will pass the regxep below
        }
        const colArray = colour.match(/[.\d]+/g).map(function (c) {
            return +c
        });

        return {
            r: colArray[0],
            g: colArray[1],
            b: colArray[2],
            a: colArray[3],
        };
    }

    throw new Error(`Could not parse CSS/SVG colour string '${colour}' to RGBA!`);
}

export function panelColourStringToCmyk(colourString: string): ICMYKAColour {
    const ret: ICMYKAColour = {
        c: 0,
        m: 0,
        y: 0,
        k: 0,
        a: 0,
    }

    const spl = colourString.split('|');
    const type = spl[0].toUpperCase()
    switch (type) {
        case "REGISTRATION":
            {
                // The power curve applies gamma to the registration value, more closely approximating the output colour.
                const regValPow = Math.pow(parseFloat(spl[1]) / 100.0, 2.2);
                ret.c = regValPow;
                ret.m = regValPow;
                ret.y = regValPow;
                ret.k = regValPow;
                ret.a = 1.0;
            }
            break;

        case "PROCESSCOL":
        case "CUSTOMSPOT":
            ret.c = parseFloat(spl[2]);
            ret.m = parseFloat(spl[3]);
            ret.y = parseFloat(spl[4]);
            ret.k = parseFloat(spl[5]);
            ret.a = parseFloat(spl[6]);
            break;

        case "SPOTCOL":
            {
                const col = coloursServiceInstance.GetSpotColourWithIdOrName(spl[1]);
                const t = spl.length > 3 ? parseFloat(spl[3]) : 1.0;
                if (col) {
                    ret.c = col.c * t;
                    ret.m = col.m * t;
                    ret.y = col.y * t;
                    ret.k = col.k * t;
                    ret.a = parseFloat(spl[2]);
                }
            }
            break;

        case "NONE":
            break;

        default:
            throw new Error(`Unknown PD colour string type ${type}`);
    }

    return ret;

}

export function panelColourStringToRgba(colourString: string): IRGBAColour {
    const cmyk = panelColourStringToCmyk(colourString);
    const rgb = cmykToRgb(cmyk.c, cmyk.m, cmyk.y, cmyk.k);

    const ret: IRGBAColour = {
        r: rgb.r,
        g: rgb.g,
        b: rgb.b,
        a: cmyk.a,
    }
    return ret;
}

export function panelColourStringToSvgColourString(colourString: string): string {
    return rgbaToCss(panelColourStringToRgba(colourString));
}

export function parsePanelColourString(colourString: string): IColourData {
    let ret: IColourData;

    const spl = colourString.split('|');
    const type = spl[0].toUpperCase()
    switch (type) {
        case "REGISTRATION":
            ret = {
                type: ColourType.Registration,
                reg: parseFloat(spl[1]),
                alpha: spl.length >= 2 ? parseFloat(spl[2]) : 1.0,
            };
            break;

        case "PROCESSCOL":
            ret = {
                type: ColourType.ProcessColour,
                c: parseFloat(spl[2]),
                m: parseFloat(spl[3]),
                y: parseFloat(spl[4]),
                k: parseFloat(spl[5]),
                alpha: parseFloat(spl[6]),
            };
            break;

        case "CUSTOMSPOT":
            ret = {
                type: ColourType.CustomSpot,
                name: decodeSpotName(spl[1]),
                c: parseFloat(spl[2]),
                m: parseFloat(spl[3]),
                y: parseFloat(spl[4]),
                k: parseFloat(spl[5]),
                alpha: parseFloat(spl[6]),
            };
            break;

        case "SPOTCOL":
            ret = {
                type: ColourType.RegisteredSpot,
                spotID: spl[1],
                alpha: parseFloat(spl[2]),
                tint: parseFloat(spl.length > 3 ? spl[3] : "1"),
            };
            break;

        case "NONE":
            ret = {
                type: ColourType.None,
            };
            break;

        default:
            throw new Error(`Unknown PD colour string type ${spl[0]}`);
    }

    return ret;
}

export function panelColourStringFromColour(colour: IColourData): string {
    switch (colour.type) {
        case ColourType.Registration:
            return `REGISTRATION|${colour.reg}`;

        case ColourType.RegisteredSpot:
            return `SPOTCOL|${colour.spotID}|${colour.alpha}|${colour.tint}`;

        case ColourType.ProcessColour:
            return `PROCESSCOL|~|${colour.c}|${colour.m}|${colour.y}|${colour.k}|${colour.alpha}`;

        case ColourType.CustomSpot:
            return `CUSTOMSPOT|${encodeSpotName(colour.name)}|${colour.c}|${colour.m}|${colour.y}|${colour.k}|${colour.alpha}`;

        default:
            return `NONE`;
    }
}

function encodeSpotName(spotName: string): string {
    return spotName.replace(/\|/g, "&vert;");
}

function decodeSpotName(spotName:string): string {
    return spotName.replace(/&vert;/g, "|");
}
